import {
  useCallback,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { cn } from '~/utils/common';

type ComponentProps = {
  icon?: 'arrow' | 'plus';
  title: ReactNode;
  titleClassName?: string;
  contentClassName?: string;
  open?: boolean;
  onToggle: () => void;
};

export type CollapseProps = ComponentProps &
  Omit<ComponentPropsWithoutRef<'div'>, 'title'>;

export function Collapse({
  icon,
  title,
  className,
  titleClassName,
  contentClassName,
  open,
  onToggle,
  children,
}: CollapseProps) {
  return (
    <div
      className={cn(
        {
          'collapse-arrow': icon === 'arrow',
          'collapse-plus': icon === 'plus',
        },
        className,
      )}
    >
      <button
        type="button"
        className={cn('collapse-title p-0', titleClassName)}
        onClick={() => onToggle()}
      >
        {title}
      </button>
      {open && <div className={cn('py-4', contentClassName)}>{children}</div>}
    </div>
  );
}

export function CollapsibleCard({
  title,
  defaultOpen,
  ...collapseProps
}: Omit<CollapseProps, 'open' | 'onToggle'> & { defaultOpen?: boolean }) {
  const controls = useCollapseControl(defaultOpen);

  return (
    <ControlledCollapsibleCard title={title} {...collapseProps} {...controls} />
  );
}

export function ControlledCollapsibleCard({
  title,
  ...collapseProps
}: CollapseProps) {
  return (
    <div className="card card-border shadow-sm">
      <div className="card-body py-2">
        <Collapse
          icon="arrow"
          title={
            <h3 className="card-title flex justify-between items-center gap-4">
              {title}
            </h3>
          }
          {...collapseProps}
        />
      </div>
    </div>
  );
}

export function useCollapseControl(initialOpen = false) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);

  return {
    open: isOpen,
    setIsOpen,
    onToggle: toggleOpen,
  };
}
